import React, { useEffect, useRef, useState } from 'react';

/**
 * SequencingTest — MOBA Module 2 (replaces Aim)
 * 20 rounds. 2–4 numbered circles appear simultaneously.
 * Must click in numbered order (1→2→3→4) within 3500ms.
 * Wrong-order click immediately fails the round.
 * Empty-space click is ignored; window continues.
 */
export default function SequencingTest({ trials, onComplete, icon, moduleNum = 2, totalModules = 6 }) {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    roundIndex: 0,
    nextExpected: 1,
    roundStartTime: null,
    lastClickTime: null,
    clickTimes: [],
    results: [],
    timeoutId: null,
    running: false,
  });

  const runNextRoundRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const warmupPhase = useRef(true);
  const [warmupComplete, setWarmupComplete] = useState(false);

  const [uiState, setUiState] = useState('instructions');
  const [progress, setProgress] = useState(0);

  const WARMUP_ROUNDS = [
    { targets: [{ number: 1, x: 0.35, y: 0.45, radius: 42 }, { number: 2, x: 0.65, y: 0.55, radius: 42 }], isi: 700, windowMs: 5000 },
    { targets: [{ number: 1, x: 0.4, y: 0.3, radius: 42 }, { number: 2, x: 0.6, y: 0.7, radius: 42 }], isi: 700, windowMs: 5000 },
  ];

  useEffect(() => {
    document.body.style.cursor = uiState === 'running' ? 'crosshair' : '';
    return () => { document.body.style.cursor = ''; };
  }, [uiState]);

  // --- Drawing ---

  const COLORS = {
    default: '#00d4ff',
    completed: '#10b981',
    failed: '#ef4444',
  };

  const drawRound = (ctx, canvas, targets, nextExpected, flash = null) => {
    ctx.fillStyle = '#0a0e1a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    for (const target of targets) {
      const cx = target.x * canvas.width;
      const cy = target.y * canvas.height;
      const radius = target.radius || 32;
      const isCompleted = target.number < nextExpected;

      let color;
      if (flash === 'success') color = COLORS.completed;
      else if (flash === 'fail') color = COLORS.failed;
      else color = isCompleted ? COLORS.completed : COLORS.default;

      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fillStyle = color + '33'; // translucent fill
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      ctx.stroke();

      // Number label
      ctx.fillStyle = isCompleted ? '#10b981' : '#ffffff';
      ctx.font = `bold ${Math.round(radius * 0.75)}px monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(target.number), cx, cy);
    }

    // Instruction hint
    if (!flash) {
      ctx.fillStyle = 'rgba(139,154,181,0.7)';
      ctx.font = '13px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(`Click in order: 1 → ${targets.length}`, canvas.width / 2, 12);
    }
  };

  const clearCanvas = (ctx, canvas) => {
    ctx.fillStyle = '#0a0e1a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  // --- Core logic ---

  runNextRoundRef.current = () => {
    const s = stateRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const activeTrials = warmupPhase.current ? WARMUP_ROUNDS : trials;

    if (s.roundIndex >= activeTrials.length) {
      if (warmupPhase.current) {
        s.running = false;
        setWarmupComplete(true);
        setTimeout(() => {
          warmupPhase.current = false;
          s.roundIndex = 0;
          s.results = [];
          s.running = false;
          setWarmupComplete(false);
          setProgress(0);
          setTimeout(() => runNextRoundRef.current?.(), 200);
        }, 2000);
        return;
      }
      s.running = false;
      setUiState('done');
      onCompleteRef.current(s.results);
      return;
    }

    const round = activeTrials[s.roundIndex];
    s.nextExpected = 1;
    s.clickTimes = [];
    s.lastClickTime = null;
    s.running = true;
    if (!warmupPhase.current) {
      setProgress(Math.round((s.roundIndex / trials.length) * 100));
    }

    clearCanvas(ctx, canvas);

    // ISI blank, then targets appear
    s.timeoutId = setTimeout(() => {
      s.roundStartTime = performance.now();
      s.lastClickTime = s.roundStartTime;
      requestAnimationFrame(() => drawRound(ctx, canvas, round.targets, s.nextExpected));

      // Window timeout — auto-fail
      s.timeoutId = setTimeout(() => {
        if (!s.running) return;
        s.running = false;

        const roundTime = performance.now() - s.roundStartTime;
        const meanRT = s.clickTimes.length
          ? s.clickTimes.reduce((a, b) => a + b, 0) / s.clickTimes.length
          : null;

        // Only record results during real test
        if (!warmupPhase.current) {
          s.results.push({
            type: 'sequencing',
            completed: false,
            clickCount: s.nextExpected - 1,
            totalTargets: round.targets.length,
            meanRT,
            roundTime: Math.round(roundTime),
          });
        }
        s.roundIndex++;

        requestAnimationFrame(() => drawRound(ctx, canvas, round.targets, s.nextExpected, 'fail'));
        s.timeoutId = setTimeout(() => runNextRoundRef.current?.(), 400);
      }, round.windowMs || 3500);
    }, round.isi || 800);
  };

  // --- Input handlers ---

  useEffect(() => {
    if (uiState !== 'running') return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const getCanvasCoords = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
      };
    };

    const handleInput = (clientX, clientY) => {
      const s = stateRef.current;
      if (!s.running || s.roundStartTime === null) return;

      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      const activeTrials = warmupPhase.current ? WARMUP_ROUNDS : trials;
      const round = activeTrials[s.roundIndex];
      if (!round) return;

      const { x, y } = getCanvasCoords(clientX, clientY);

      // Determine which target was hit
      let hitTarget = null;
      for (const target of round.targets) {
        const cx = target.x * canvas.width;
        const cy = target.y * canvas.height;
        const radius = target.radius || 32;
        const dx = x - cx;
        const dy = y - cy;
        if (Math.sqrt(dx * dx + dy * dy) <= radius) {
          hitTarget = target;
          break;
        }
      }

      // Empty-space click — ignore
      if (!hitTarget) return;

      const now = performance.now();
      const interClickRT = Math.round(now - (s.lastClickTime || s.roundStartTime));

      if (hitTarget.number === s.nextExpected) {
        // Correct order
        s.clickTimes.push(interClickRT);
        s.lastClickTime = now;
        s.nextExpected++;

        requestAnimationFrame(() => drawRound(ctx, canvas, round.targets, s.nextExpected));

        if (s.nextExpected > round.targets.length) {
          // Round complete
          s.running = false;
          clearTimeout(s.timeoutId);

          const roundTime = now - s.roundStartTime;
          const meanRT = s.clickTimes.reduce((a, b) => a + b, 0) / s.clickTimes.length;

          // Only record results during real test
          if (!warmupPhase.current) {
            s.results.push({
              type: 'sequencing',
              completed: true,
              clickCount: round.targets.length,
              totalTargets: round.targets.length,
              meanRT: Math.round(meanRT),
              roundTime: Math.round(roundTime),
            });
          }
          s.roundIndex++;

          requestAnimationFrame(() => drawRound(ctx, canvas, round.targets, s.nextExpected, 'success'));
          s.timeoutId = setTimeout(() => runNextRoundRef.current?.(), 400);
        }
      } else {
        // Wrong order — fail round immediately
        s.running = false;
        clearTimeout(s.timeoutId);

        const roundTime = now - s.roundStartTime;
        const meanRT = s.clickTimes.length
          ? s.clickTimes.reduce((a, b) => a + b, 0) / s.clickTimes.length
          : null;

        // Only record results during real test
        if (!warmupPhase.current) {
          s.results.push({
            type: 'sequencing',
            completed: false,
            clickCount: s.nextExpected - 1,
            totalTargets: round.targets.length,
            meanRT,
            roundTime: Math.round(roundTime),
          });
        }
        s.roundIndex++;

        requestAnimationFrame(() => drawRound(ctx, canvas, round.targets, s.nextExpected, 'fail'));
        s.timeoutId = setTimeout(() => runNextRoundRef.current?.(), 500);
      }
    };

    const handleMouseDown = (e) => handleInput(e.clientX, e.clientY);
    const handleTouchStart = (e) => {
      e.preventDefault();
      const touch = e.changedTouches[0];
      handleInput(touch.clientX, touch.clientY);
    };

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('touchstart', handleTouchStart);
    };
  }, [uiState]); // eslint-disable-line

  // --- Cleanup ---

  useEffect(() => {
    return () => {
      clearTimeout(stateRef.current.timeoutId);
    };
  }, []);

  // --- Start ---

  const startTest = () => {
    stateRef.current.roundIndex = 0;
    stateRef.current.results = [];
    stateRef.current.running = false;
    setProgress(0);
    setUiState('running');
    setTimeout(() => runNextRoundRef.current?.(), 500);
  };

  const startWarmup = () => {
    warmupPhase.current = true;
    startTest();
  };

  // --- Render ---

  return (
    <div style={{ width: '100%' }}>
      {uiState === 'instructions' && (
        <div className="card text-center" style={{ maxWidth: 480, margin: '0 auto' }}>
          {icon && <div style={{ width: 56, height: 56, borderRadius: 14, background: 'var(--accent-dim)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', margin: '0 auto 20px' }}>{icon}</div>}
          <h2 style={{ marginBottom: 8 }}>Target Sequencing</h2>
          <p style={{ marginBottom: 8 }}>
            Multiple numbered targets appear at once. Click them in order —
            <strong style={{ color: 'var(--accent)' }}> 1 → 2 → 3 → 4</strong> — as fast as possible.
          </p>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 4 }}>
            Input: <span className="text-accent mono">CLICK / TAP</span>
          </p>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 4 }}>
            Wrong order = round over.
          </p>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 24 }}>
            20 rounds — complete the sequence within the 3.5s window.
          </p>
          <button className="btn btn--primary" onClick={startWarmup}>
            Start
          </button>
        </div>
      )}

      {uiState === 'running' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="progress-bar" style={{ flex: 1 }}>
              <div className="progress-bar__fill" style={{ width: `${progress}%` }} />
            </div>
            <span style={{ fontSize: 13, whiteSpace: 'nowrap', color: warmupPhase.current && !warmupComplete ? '#f59e0b' : 'var(--text-muted)' }}>
              {warmupPhase.current && !warmupComplete ? 'PRACTICE — not scored' : `${progress}%`}
            </span>
          </div>
          <div style={{ position: 'relative' }}>
            <div className="canvas-wrapper">
              <canvas
                ref={canvasRef}
                width={800}
                height={520}
                style={{ width: '100%', maxWidth: 800, background: '#0a0e1a', cursor: 'crosshair', display: 'block' }}
              />
            </div>
            {warmupComplete && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(10,14,26,0.92)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                borderRadius: 8,
              }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#10b981', marginBottom: 8 }}>
                  Warmup Complete!
                </div>
                <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }}>
                  Starting real test in 2 seconds...
                </div>
              </div>
            )}
          </div>
          <p className="text-center text-muted" style={{ fontSize: 13 }}>
            Module {moduleNum} of {totalModules} — Target Sequencing
          </p>
        </div>
      )}
    </div>
  );
}
